import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Landmark, CheckCircle2, XCircle, Loader2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { ApiError } from '@ooh/contracts';
import type { AdminBoundaryImportStatus } from '@ooh/contracts';
import { Button } from '@ooh/ui/components/button';
import { adminBoundaryQueries } from '../api/queries';
import type { WithCallWithAuth } from '../api/queries';
import { useTriggerAdminBoundaryImport } from '../api/mutations';

// ─── Status badge ─────────────────────────────────────────────

const STATUS_CONFIG: Record<
  AdminBoundaryImportStatus['status'],
  { icon: React.ElementType; className: string }
> = {
  idle: { icon: Clock, className: 'text-muted-foreground' },
  running: { icon: Loader2, className: 'text-blue-500' },
  completed: { icon: CheckCircle2, className: 'text-green-600' },
  failed: { icon: XCircle, className: 'text-destructive' },
};

function useStatusLabel(status: AdminBoundaryImportStatus['status']): string {
  const { t } = useTranslation('map');
  if (status === 'running') return t('admin.adminBoundaries.importTrigger.statusRunning');
  if (status === 'completed') return t('admin.adminBoundaries.importTrigger.statusCompleted');
  if (status === 'failed') return t('admin.adminBoundaries.importTrigger.statusFailed');
  return t('admin.adminBoundaries.importTrigger.statusIdle');
}

function StatusBadge({ status }: { status: AdminBoundaryImportStatus['status'] }) {
  const { icon: Icon, className } = STATUS_CONFIG[status];
  const label = useStatusLabel(status);

  return (
    <span
      className={`flex items-center gap-1 text-[13px] font-medium ${className}`}
      data-testid="admin-boundary-import-status-badge"
    >
      <Icon
        className={`h-3.5 w-3.5 ${status === 'running' ? 'animate-spin' : ''}`}
        strokeWidth={1.8}
        data-testid="admin-boundary-import-status-icon"
      />
      {label}
    </span>
  );
}

// ─── Status info section ──────────────────────────────────────

function StatusDetail({ importStatus }: { importStatus: AdminBoundaryImportStatus | undefined }) {
  const { t } = useTranslation('map');

  if (importStatus?.status === 'completed' && importStatus.inserted !== null) {
    return (
      <p className="text-[12px] text-muted-foreground mt-1">
        {t('admin.adminBoundaries.importTrigger.resultSummary', {
          inserted: importStatus.inserted,
          skipped: importStatus.skipped ?? 0,
        })}
      </p>
    );
  }

  if (importStatus?.status === 'failed' && importStatus.lastError) {
    return (
      <p className="text-[12px] text-destructive mt-1 break-all">
        {t('admin.adminBoundaries.importTrigger.lastError')}: {importStatus.lastError}
      </p>
    );
  }

  return null;
}

function StatusInfo({
  importStatus,
  isLoading,
  lastRunLabel,
}: {
  importStatus: AdminBoundaryImportStatus | undefined;
  isLoading: boolean;
  lastRunLabel: string | null;
}) {
  const { t } = useTranslation('map');

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-3">
        {isLoading ? (
          <div className="h-5 w-20 rounded bg-muted animate-pulse" />
        ) : (
          <StatusBadge status={importStatus?.status ?? 'idle'} />
        )}
        {lastRunLabel && !isLoading ? (
          <span className="text-[12px] text-muted-foreground">
            {t('admin.adminBoundaries.importTrigger.lastRunAt')}: {lastRunLabel}
          </span>
        ) : null}
      </div>
      <StatusDetail importStatus={importStatus} />
    </div>
  );
}

// ─── Import panel ─────────────────────────────────────────────

interface AdminBoundaryImportPanelProps {
  auth: WithCallWithAuth;
}

export function AdminBoundaryImportPanel({ auth }: AdminBoundaryImportPanelProps) {
  const { t, i18n } = useTranslation('map');

  const {
    data: importStatus,
    isLoading,
    refetch,
  } = useQuery({
    ...adminBoundaryQueries.importStatus(auth),
    refetchOnWindowFocus: false,
    refetchInterval: (query) => (query.state.data?.status === 'running' ? 5_000 : false),
  });

  const { mutate: triggerImport, isPending } = useTriggerAdminBoundaryImport(auth);

  const isRunning = importStatus?.status === 'running' || isPending;

  const lastRunLabel = React.useMemo(() => {
    if (!importStatus?.lastRunAt) return null;
    return new Date(importStatus.lastRunAt).toLocaleString(i18n.language, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  }, [importStatus?.lastRunAt, i18n.language]);

  function handleTrigger() {
    triggerImport(undefined, {
      onSuccess: () => {
        void refetch();
      },
      onError: (error) => {
        if (error instanceof ApiError && error.status === 409) {
          toast.error(t('admin.adminBoundaries.importTrigger.triggerConflict'));
        } else {
          toast.error(t('admin.adminBoundaries.importTrigger.triggerError'));
        }
      },
    });
  }

  return (
    <div
      className="rounded-xl border border-border bg-card p-5 space-y-3"
      data-testid="admin-boundary-import-panel"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-[14px] font-semibold text-foreground flex items-center gap-2">
            <Landmark className="h-4 w-4 text-muted-foreground" strokeWidth={1.6} />
            {t('admin.adminBoundaries.importTrigger.title')}
          </h3>
          <p className="text-[12px] text-muted-foreground max-w-lg">
            {t('admin.adminBoundaries.importTrigger.description')}
          </p>
          <StatusInfo
            importStatus={importStatus}
            isLoading={isLoading}
            lastRunLabel={lastRunLabel}
          />
        </div>

        <Button
          size="xs"
          onClick={handleTrigger}
          disabled={isRunning}
          data-testid="trigger-admin-boundary-import-button"
        >
          {isRunning
            ? t('admin.adminBoundaries.importTrigger.triggerButtonRunning')
            : t('admin.adminBoundaries.importTrigger.triggerButton')}
        </Button>
      </div>
    </div>
  );
}
